import { EditorNodeName } from "@/enums/customAttributes";
import { parseFormula } from "@/helpers/parser";
import {
  createEmptyEditorNode,
  tokenArrayToString,
} from "@/helpers/parser/helpers";
import { mergeNodeIdentities } from "@/helpers/parser/merge";

import type { ParseResult } from "@/helpers/parser";
import type { EditorNode } from "@/models/TextEditor";
import type { FormulaOption } from "@/models/options";
import type { IAttribute } from "@/models/orchestration";

export interface SerializerOptions {
  preserveIdentities?: boolean;
  fallbackOnError?: boolean;
}

export const useEditorSerializer = (
  functionsByNameMap: () => Record<string, FormulaOption>,
  serviceAttributes: () => IAttribute[]
) => {
  const serialize = (nodes: EditorNode[]): string => {
    if (!nodes?.length) {
      return "";
    }

    const filtered = nodes.filter(
      (node) =>
        node &&
        ((node.content?.trim().length ?? 0) > 0 ||
          node.atomic ||
          node.type === EditorNodeName.whitespace)
    );

    return tokenArrayToString(filtered);
  };
  const createEmptyResult = (): {
    nodes: EditorNode[];
    parseResult: ParseResult;
  } => ({
    nodes: [createEmptyEditorNode()],
    parseResult: {
      tokens: [],
      metadata: {
        bracketPairs: new Map(),
        functionCalls: [],
        totalTokens: 0,
      },
      errors: [],
    },
  });

  const deserialize = (
    formula: string,
    oldNodes: EditorNode[] = [],
    options: SerializerOptions = {}
  ): { nodes: EditorNode[]; parseResult: ParseResult } => {
    const { preserveIdentities = true, fallbackOnError = true } = options;

    if (!formula.trim()) {
      return createEmptyResult();
    }

    const parseResult = parseFormula(
      formula,
      functionsByNameMap(),
      serviceAttributes()
    );

    const hasNoTokens = !parseResult.tokens.length;
    const shouldFallbackToOldNodes =
      hasNoTokens && fallbackOnError && oldNodes.length;

    if (shouldFallbackToOldNodes) {
      return { nodes: oldNodes, parseResult };
    }

    let resultNodes = hasNoTokens
      ? [createEmptyEditorNode()]
      : parseResult.tokens;

    if (preserveIdentities && oldNodes.length) {
      resultNodes = mergeNodeIdentities(oldNodes, resultNodes);
    }

    return { nodes: resultNodes, parseResult };
  };

  const serializeRange = (
    nodes: EditorNode[],
    startIndex: number,
    endIndex: number
  ): string => {
    if (startIndex < 0 || endIndex > nodes.length || startIndex >= endIndex) {
      return "";
    }

    const rangeNodes = nodes.slice(startIndex, endIndex);
    return serialize(rangeNodes);
  };

  return {
    serialize,
    deserialize,
    serializeRange,
  };
};
